import type { RadarComponentSpec, RadarMetricEntry, WidgetResponseSpec, ProcessedRadarData } from "./types";

const AGG_SHORT: Record<string, string> = { avg: "[A]", sum: "[S]", best: "[B]" };

// Radar y-axis floor (negative to fit z-score normalization). The center of the
// pane maps to this value, so area fills must use it as their threshold — the
// default threshold of 0 leaves the inner ring (center → y=0) unfilled.
const RADAR_Y_MIN = -25;

// Draws gradient line segments (and optional fill wedges) between adjacent colored markers,
// replacing the default Highcharts line/area. Only applied to series without _hasColorOverride.
function applySegmentGradients(chart: any): void {
  const svg: SVGElement | null = chart.container.querySelector("svg");
  if (!svg) return;

  let defs = svg.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    svg.insertBefore(defs, svg.firstChild);
  }

  // Center of the polar pane in SVG coordinates.
  const cx = chart.plotLeft + chart.plotWidth / 2;
  const cy = chart.plotTop + chart.plotHeight / 2;

  chart.series.forEach((series: any, si: number) => {
    svg.querySelectorAll(`[data-rsg="${si}"]`).forEach((el: Element) => el.remove());
    defs!.querySelectorAll(`[data-rgd="${si}"]`).forEach((el: Element) => el.remove());

    if (series.options._hasColorOverride) return;
    if (!series.visible) return;

    const points: any[] = series.points ?? [];
    if (!points.length) return;

    const colors: (string | undefined)[] = points.map(
      (p: any) => p.options?.marker?.fillColor as string | undefined,
    );
    if (colors.every((c) => !c)) return;

    // Hide Highcharts' own line and fill — we replace both below.
    series.graph?.attr({ visibility: "hidden" });
    series.area?.attr({ visibility: "hidden" });

    const n = points.length;
    const isFill = series.type === "area";
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
    g.setAttribute("data-rsg", String(si));

    for (let i = 0; i < n; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % n];
      if (p1.plotX == null || p2.plotX == null) continue;

      const x1 = p1.plotX + chart.plotLeft;
      const y1 = p1.plotY + chart.plotTop;
      const x2 = p2.plotX + chart.plotLeft;
      const y2 = p2.plotY + chart.plotTop;

      const c1 = colors[i] ?? "#999";
      const c2 = colors[(i + 1) % n] ?? "#999";
      // chart.index is unique per Highcharts instance in the DOM, preventing
      // url(#id) from resolving to a gradient belonging to another radar chart.
      const gradId = `rg-${chart.index}-${si}-${i}`;

      // One gradient definition reused by both the fill wedge and the line segment.
      const grad = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
      grad.setAttribute("id", gradId);
      grad.setAttribute("gradientUnits", "userSpaceOnUse");
      grad.setAttribute("x1", String(x1));
      grad.setAttribute("y1", String(y1));
      grad.setAttribute("x2", String(x2));
      grad.setAttribute("y2", String(y2));
      grad.setAttribute("data-rgd", String(si));

      const stop1 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
      stop1.setAttribute("offset", "0%");
      stop1.setAttribute("stop-color", c1);
      const stop2 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
      stop2.setAttribute("offset", "100%");
      stop2.setAttribute("stop-color", c2);
      grad.appendChild(stop1);
      grad.appendChild(stop2);
      defs!.appendChild(grad);

      // Fill wedge: center → p1 → p2, drawn before the line so the line sits on top.
      if (isFill) {
        const wedge = document.createElementNS("http://www.w3.org/2000/svg", "path");
        wedge.setAttribute("d", `M ${cx} ${cy} L ${x1} ${y1} L ${x2} ${y2} Z`);
        wedge.setAttribute("fill", `url(#${gradId})`);
        wedge.setAttribute("fill-opacity", "0.15");
        wedge.setAttribute("stroke", "none");
        g.appendChild(wedge);
      }

      // Gradient line segment.
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", `M ${x1} ${y1} L ${x2} ${y2}`);
      path.setAttribute("stroke", `url(#${gradId})`);
      path.setAttribute("stroke-width", "2");
      path.setAttribute("fill", "none");
      g.appendChild(path);
    }

    // Insert before tooltip/legend/exporting so our gradient elements don't
    // occlude them after legend-click redraws re-anchor those groups in the SVG.
    const insertBeforeEl = svg.querySelector(
      ".highcharts-tooltip, .highcharts-legend, .highcharts-exporting-group",
    );
    if (insertBeforeEl) {
      svg.insertBefore(g, insertBeforeEl);
    } else {
      svg.appendChild(g);
    }
  });
}

export function processRadar(
  spec: RadarComponentSpec,
  response: WidgetResponseSpec | null,
): ProcessedRadarData {
  if (!response || !spec.metric_names.length) {
    return { chartOptions: buildEmptyOptions(spec) };
  }

  const misc = spec.radar_builder_misc ?? {};
  const radarMetrics = (misc.metrics ?? []) as RadarMetricEntry[];
  const vertexDimension = spec.vertexDimension ?? (misc.vertexDimension as string | undefined) ?? "metric";

  const responseById = new Map(response.series.map((r) => [r.query_id, r]));

  const makeGetValue = (useHexMarkers: boolean) => (qid: string) => {
    const r = responseById.get(qid);
    if (!r || !r.data.length) return null;
    const point = r.data[0];
    return {
      y: point.value,
      custom: { rawValue: point.value_label ?? String(point.value) },
      ...(useHexMarkers && point.hex && {
        marker: { fillColor: point.hex, lineColor: point.hex, enabled: true, radius: 5 },
      }),
    };
  };

  let xAxisCategories: string[];
  let highchartsSeries: Record<string, unknown>[];

  if (vertexDimension === "metric") {
    xAxisCategories = spec.metric_names;
    highchartsSeries = spec.series.map((seriesDef) => {
      const getValue = makeGetValue(false);
      return {
        type: seriesDef.fillArea ? "area" : "line",
        name: seriesDef.displayNameOverride ?? seriesDef.displayName,
        data: seriesDef.queryIds.map(getValue),
        pointPlacement: "on",
        _hasColorOverride: !!seriesDef.colorOverride,
        ...(seriesDef.colorOverride && { color: seriesDef.colorOverride }),
        ...(seriesDef.fillArea && { fillOpacity: 0.15, threshold: RADAR_Y_MIN }),
      };
    });
  } else {
    xAxisCategories = spec.series.map((s) => s.displayNameOverride ?? s.displayName);
    highchartsSeries = radarMetrics.map((metric, j) => {
      const getValue = makeGetValue(false);
      const defaultName = `${AGG_SHORT[metric.metricAgg] ?? "[A]"} ${metric.displayName}`;
      return {
        type: metric.fillArea ? "area" : "line",
        name: metric.displayNameOverride ?? defaultName,
        data: spec.series.map((s) => getValue(s.queryIds[j])),
        pointPlacement: "on",
        _hasColorOverride: !!metric.colorOverride,
        ...(metric.colorOverride && { color: metric.colorOverride }),
        ...(metric.fillArea && { fillOpacity: 0.15, threshold: RADAR_Y_MIN }),
      };
    });
  }

  return {
    chartOptions: {
      chart: {
        polar: true,
        type: "line",
        animation: false,
        style: { fontFamily: "inherit" },
        events: {
          render(this: any) { applySegmentGradients(this); },
        },
      },
      title: { text: null },
      pane: { size: "75%" },
      xAxis: {
        categories: xAxisCategories,
        tickmarkPlacement: "on",
        lineWidth: 0,
        labels: { style: { fontSize: "13px" } },
      },
      yAxis: {
        gridLineInterpolation: "polygon",
        lineWidth: 0,
        min: RADAR_Y_MIN,
        max: 100,
        tickInterval: 25,
        labels: { enabled: false },
      },
      legend: {
        enabled: spec.display.showLegend,
        align: "center",
        verticalAlign: "bottom",
      },
      tooltip: {
        shared: true,
        headerFormat: "<b>{point.key}</b><br/>",
        pointFormat:
          '<span style="color:{point.color}">●</span> {series.name}: <b>{point.custom.rawValue}</b><br/>',
      },
      plotOptions: {
        series: {
          animation: false,
          marker: { enabled: true, radius: 4 },
        },
      },
      series: highchartsSeries,
      credits: { enabled: false },
    },
  };
}

function buildEmptyOptions(spec: RadarComponentSpec): Record<string, unknown> {
  return {
    chart: { polar: true, type: "line" },
    title: { text: spec.display.title || null },
    xAxis: { categories: spec.metric_names },
    yAxis: { min: 0, max: 100 },
    series: [],
    credits: { enabled: false },
  };
}
